import { useEffect, useRef, useState } from 'react'
import { useActiveIncident } from './useActiveIncident'
import type { IncidentResponse } from '../types'

// ─── Sound ────────────────────────────────────────────────────────────────────

function playAlertSound() {
  try {
    const ctx = new AudioContext()

    const tone = (freq: number, startSec: number, durationSec: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.type = 'sine'
      osc.frequency.value = freq
      const t0 = ctx.currentTime + startSec
      gain.gain.setValueAtTime(0, t0)
      gain.gain.linearRampToValueAtTime(0.35, t0 + 0.015)
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + durationSec)
      osc.start(t0)
      osc.stop(t0 + durationSec + 0.05)
    }

    // Ascending alert arpeggio: E5 → A5 → C6
    tone(659, 0.0, 0.22)
    tone(880, 0.2, 0.22)
    tone(1047, 0.4, 0.45)

    setTimeout(() => ctx.close(), 1500)
  } catch {
    // AudioContext blocked or unsupported — silent fail
  }
}

// ─── Browser notification ─────────────────────────────────────────────────────

function showBrowserNotification(subject?: string | null) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  try {
    new Notification('⚠️ Active Incident', {
      body: subject ?? 'A new on-call incident has been detected.',
      icon: '/icon-192.png',
      tag: 'oncall-incident',
      requireInteraction: true,
    })
  } catch {
    // ignore
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Detects new active incidents from the 30-second polling cycle and:
 * - plays an audio alert
 * - shows a browser notification
 * - returns `popupIncident` for displaying an in-app snackbar
 *
 * If the popup is already open for the same incident, only the sound /
 * browser notification fires on repeat polls — the popup is NOT re-mounted.
 *
 * Call once from Layout so it stays alive on all pages.
 */
export function useIncidentAlert() {
  const { data: activeIncident } = useActiveIncident()

  const currentId   = activeIncident?.incidentId ?? null
  const emailSubject = activeIncident?.emailSubject

  // ── Popup state: derived during render (avoids setState-in-effect) ──────────
  //
  // React-recommended pattern: compare with previous render value and call
  // setState synchronously in the render body (not inside an effect).
  // React will immediately re-render with the updated state, skipping a paint.
  // https://react.dev/reference/react/useState#storing-information-from-previous-renders
  const [prevId, setPrevId] = useState<string | null | undefined>(undefined)
  const [popupIncident, setPopupIncident] = useState<IncidentResponse | null>(null)

  if (prevId !== currentId) {
    setPrevId(currentId)

    if (prevId === undefined) {
      // First successful data load — alert immediately if incident is already active
      if (currentId) {
        setPopupIncident(activeIncident ?? null)
      }
    } else if (currentId !== null && currentId !== prevId) {
      // Brand-new incident detected
      setPopupIncident(activeIncident ?? null)
    } else if (currentId === null && prevId !== null) {
      // Active incident resolved — close popup
      setPopupIncident(null)
    }
  }

  // ── Side effects: sound + browser notification ─────────────────────────────
  //
  // These are kept in a separate effect that only runs sound/notification logic,
  // with no setState calls — satisfying the react-hooks/set-state-in-effect rule.
  // prevIdForSfx tracks whether the sound has already fired for this incidentId.
  const prevIdForSfx = useRef<string | null | undefined>(undefined)

  useEffect(() => {
    if (prevIdForSfx.current === undefined) {
      // First load
      prevIdForSfx.current = currentId
      if (currentId) {
        playAlertSound()
        showBrowserNotification(emailSubject)
      }
      return
    }
    if (currentId !== null && currentId !== prevIdForSfx.current) {
      playAlertSound()
      showBrowserNotification(emailSubject)
    }
    prevIdForSfx.current = currentId
  }, [currentId, emailSubject])

  // ── Request notification permission once on mount ──────────────────────────
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  return {
    popupIncident,
    dismissPopup: () => setPopupIncident(null),
  }
}
